import {
  DESKTOP_DICTATION_FLAG,
  WEB_DICTATION_FLAG,
} from '@/features/flags/legacy-flags';
import { useFeatureFlagEnabled } from '@/features/flags/posthog-compat';
import { isTauri } from '@heidi/runtime/platform';

/**
 * The current platform's dictation rollout flag. Separate module from
 * `useIsDictationEnabled` to avoid an import cycle: that file imports
 * `useQueryUser`, which imports `@/features/flags`, which needs this hook.
 *
 * RULING 36 CLASSIFICATION — a dictation-owned named getter that STAYS
 * APP-SIDE, and the blocker is the mechanism rather than the domain.
 * `release-desktop-dictation` and `universal-dictation` are PostHog flags:
 * `useFeatureFlagEnabled` here is `posthog-compat`, which resolves from PostHog
 * on cloud and only falls back to OpenFeature on the airgapped build.
 * `@heidi/runtime/feature-flags` carries no PostHog surface, so rewriting this
 * onto the door would silently change WHICH provider answers for the gate that
 * turns dictation on — a behaviour change wearing a refactor's clothes, not a
 * move. `monorepo-wave-1-p3-feature-flags` Z4 states the standing rule this
 * follows: a flag consumed from package code must be door-readable, so
 * extractions steer flags to Flagsmith flag-by-flag. This getter follows its
 * keys into `packages/dictate/src/flags.ts` when they migrate, not before.
 *
 * It is nonetheless the ONE hook-form minting site for these two keys inside
 * the dictation roots, which is the property layer (c) actually cares about:
 * `useIsDictationEnabled` used to re-read both keys raw and now calls this.
 */
export const useIsPlatformDictationEnabled = (): boolean => {
  // PostHog-technical: this key is resolved by PostHog on cloud, and the flags
  // door has no PostHog surface, so the raw read stays until the flag migrates.
  const desktopDictationEnabled = useFeatureFlagEnabled(DESKTOP_DICTATION_FLAG) ?? false;
  // PostHog-technical, same constraint on the web half of the same gate.
  const webDictationEnabled = useFeatureFlagEnabled(WEB_DICTATION_FLAG) ?? false;
  return isTauri() ? desktopDictationEnabled : webDictationEnabled;
};
